import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { NoDataWarning } from "@/components/common/NoDataWarning";
import {
  tableActionHeaderCellStyles,
  tableHeaderCellStyles,
} from "@/components/GenericTable/styles";

import ClinicalCrmContextInfoBanner from "../../messageTemplates/components/ClinicalCrmContextInfoBanner";
import { crmPatientsFromIds } from "../helpers/mapCrmPatientSummaryToMessagingPatient";
import { useSetSelection } from "../hooks/useSetSelection";
import { useTablePagination } from "../hooks/useTablePagination";
import CommunicationsTablePagination from "./CommunicationsTablePagination";
import {
  formatCommunicationsDateTimeDisplay,
  formatPatientTaxIdentifierDisplay,
} from "../helpers/formatCrmCommunicationsDisplay";
import { formatPatientListLabel } from "../patientLabels";
import PatientNameCell from "./PatientNameCell";
import {
  PatientBirthDateTableCellContent,
  PatientContactTableCellContent,
  PatientTaxIdentifierTableCellContent,
} from "./PatientSummaryTableCells";
import type { CrmPatientSummary, FluxZapBatchTaskView } from "../types";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import CommunicationPatientSelectionBar from "./CommunicationPatientSelectionBar";
import {
  communicationsRankFilledSearchFieldSx,
  communicationsRankTableBodyCellSx,
  communicationsRankTableToolbarSx,
} from "./communicationsSubsectionStyles";
import { isVetApp } from "../../messaging/helpers/isVetApp";

/** Mesmo espírito de TEMPLATE_MODELS_PANEL_HEIGHT_PX em SendWhatsAppMessageDialog. */
const RECIPIENTS_PANEL_HEIGHT_PX = 380;

const PARTIAL_PRIVACY_WARNING_DESCRIPTION =
  "Alguns destinatários não receberam a mensagem por motivo de privacidade. Estes destinatários podem ter desabilitado o recebimento de WhatsApp no Fluxmed. Mensagens não entregues não são contabilizadas no envio pelo número padrão Fluxmed.";

export interface CommunicationsBatchTaskDetailDialogProps {
  open: boolean;
  task: FluxZapBatchTaskView | null;
  patientLookup: Map<string, CrmPatientSummary>;
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  onClose: () => void;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

const actionButtonSx = {
  height: 36,
  px: 2,
  borderRadius: 2,
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
};

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

function matchesRecipientSearch(params: {
  patientId: string;
  patient: CrmPatientSummary | undefined;
  query: string;
  petNames?: string[];
}): boolean {
  const { patientId, patient, query, petNames } = params;
  if (!query) return true;
  if (patient) {
    const label = formatPatientListLabel(patient).toLowerCase();
    const email = (patient.email ?? "").toLowerCase();
    const phone = (patient.phone ?? "").toLowerCase();
    const tax = (patient.taxIdentifierValue ?? "").toLowerCase();
    const taxDisplay = formatPatientTaxIdentifierDisplay(
      patient.taxIdentifierValue,
    ).toLowerCase();
    const petsJoined = (petNames ?? []).join(" ").toLowerCase();

    return (
      label.includes(query) ||
      email.includes(query) ||
      phone.includes(query) ||
      tax.includes(query) ||
      taxDisplay.includes(query) ||
      petsJoined.includes(query)
    );
  }
  return patientId.toLowerCase().includes(query);
}

/**
 * Detalhe do disparo (Task + destinatários) e atalhos para envio via WhatsApp.
 */
const CommunicationsBatchTaskDetailDialog = memo(
  ({
    open,
    task,
    patientLookup,
    vetApp: vetAppProp,
    petNamesByTutorLogicalId,
    onClose,
    onOpenSendMessage,
  }: CommunicationsBatchTaskDetailDialogProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const vetApp = vetAppProp ?? isVetApp();

    const { selectedIds, selectedCount, toggle, toggleAll, clear, has } =
      useSetSelection();

    const recipientIds = useMemo(
      () => (task ? [...task.recipientPatientIds] : []),
      [task],
    );

    const filteredRecipientIds = useMemo(() => {
      const q = normalizeSearch(searchQuery);
      if (!q) return recipientIds;
      return recipientIds.filter((pid) =>
        matchesRecipientSearch({
          patientId: pid,
          patient: patientLookup.get(pid),
          query: q,
          petNames: vetApp ? petNamesByTutorLogicalId?.get(pid) : undefined,
        }),
      );
    }, [recipientIds, searchQuery, patientLookup, vetApp, petNamesByTutorLogicalId]);

    const recipientListResetKey = useMemo(
      () => `${task?.taskId ?? "closed"}-${searchQuery}`,
      [task?.taskId, searchQuery],
    );

    const {
      paginatedItems: paginatedRecipientIds,
      page,
      rowsPerPage,
      totalCount: recipientCount,
      handlePageChange,
      handleRowsPerPageChange,
    } = useTablePagination(filteredRecipientIds, recipientListResetKey);

    const allSelected =
      filteredRecipientIds.length > 0 &&
      filteredRecipientIds.every((id) => selectedIds.has(id));
    const someSelected = filteredRecipientIds.some((id) => selectedIds.has(id));

    const showPartialPrivacyWarning =
      task != null && task.skippedCount > 0 && task.successCount > 0;

    useEffect(() => {
      clear();
    }, [task?.taskId, open, clear]);

    useEffect(() => {
      setSearchQuery("");
    }, [task?.taskId, open]);

    const openSendForPatientIds = useCallback(
      (ids: Iterable<string>) => {
        const patients = crmPatientsFromIds(ids, patientLookup);
        if (patients.length > 0) {
          onOpenSendMessage({
            patients,
            templateCode: task?.templateCode,
          });
        }
      },
      [onOpenSendMessage, patientLookup, task?.templateCode],
    );

    return (
      <Dialog
        open={open && task != null}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle1" fontWeight={700}>
              Detalhes do envio
            </Typography>
            {task ? (
              <Typography variant="caption" color="text.secondary">
                {task.templateLabel} ·{" "}
                {formatCommunicationsDateTimeDisplay(task.authoredOn)}
              </Typography>
            ) : null}
          </Stack>
          <IconButton
            aria-label="Fechar"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {task ? (
            <Stack spacing={1.5}>
              {showPartialPrivacyWarning ? (
                <ClinicalCrmContextInfoBanner
                  variant="warning"
                  title={
                    vetApp
                      ? "Alguns tutores não receberam a mensagem"
                      : "Alguns pacientes não receberam a mensagem"
                  }
                  icon={<WarningAmberOutlinedIcon fontSize="small" />}
                  description={PARTIAL_PRIVACY_WARNING_DESCRIPTION}
                />
              ) : null}

              {recipientIds.length > 0 ? (
                <>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {vetApp
                      ? `Tutores que receberam (${recipientIds.length})`
                      : `Pacientes que receberam (${recipientIds.length})`}
                  </Typography>

                  <CommunicationPatientSelectionBar
                    selectedCount={selectedCount}
                    onSendMessage={() => openSendForPatientIds(selectedIds)}
                    onClearSelection={clear}
                  />

                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Box sx={communicationsRankTableToolbarSx}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="filled"
                        label={vetApp ? "Buscar tutor" : "Buscar paciente"}
                        placeholder={
                          vetApp
                            ? "Nome, telefone, CPF ou pet…"
                            : "Nome, e-mail ou CPF…"
                        }
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        sx={communicationsRankFilledSearchFieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {filteredRecipientIds.length === 0 &&
                    searchQuery.trim() ? (
                      <Box sx={{ px: 1.5, py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {vetApp
                            ? "Nenhum tutor corresponde à busca."
                            : "Nenhum paciente corresponde à busca."}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <TableContainer>
                          <Table
                            size="small"
                            aria-label={
                              vetApp
                                ? "Tutores que receberam"
                                : "Pacientes que receberam"
                            }
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  padding="checkbox"
                                  sx={tableHeaderCellStyles}
                                  align="center"
                                >
                                  <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={
                                      someSelected && !allSelected
                                    }
                                    onChange={() =>
                                      toggleAll(filteredRecipientIds)
                                    }
                                    inputProps={{
                                      "aria-label": vetApp
                                        ? "Selecionar todos os tutores que receberam"
                                        : "Selecionar todos os pacientes que receberam",
                                    }}
                                  />
                                </TableCell>
                                <TableCell
                                  sx={tableHeaderCellStyles}
                                  align="center"
                                >
                                  <Typography variant="subtitle2">
                                    {vetApp ? "Tutor" : "Paciente"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={tableHeaderCellStyles}
                                  align="center"
                                >
                                  <Typography variant="subtitle2">
                                    Contato
                                  </Typography>
                                </TableCell>
                                {!vetApp ? (
                                  <TableCell
                                    sx={tableHeaderCellStyles}
                                    align="center"
                                  >
                                    <Typography variant="subtitle2">
                                      Nascimento
                                    </Typography>
                                  </TableCell>
                                ) : (
                                  <TableCell
                                    sx={tableHeaderCellStyles}
                                    align="center"
                                  >
                                    <Typography variant="subtitle2">
                                      Pet(s)
                                    </Typography>
                                  </TableCell>
                                )}
                                <TableCell
                                  sx={tableHeaderCellStyles}
                                  align="center"
                                >
                                  <Typography variant="subtitle2">CPF</Typography>
                                </TableCell>
                                <TableCell
                                  sx={tableActionHeaderCellStyles}
                                  align="center"
                                >
                                  <Typography variant="subtitle2">
                                    Ações
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedRecipientIds.map((pid) => {
                                const p = patientLookup.get(pid);
                                const petNames = vetApp
                                  ? petNamesByTutorLogicalId?.get(pid)
                                  : undefined;
                                const checked = has(pid);
                                return (
                                  <TableRow key={pid} hover selected={checked}>
                                    <TableCell
                                      padding="checkbox"
                                      align="center"
                                      sx={communicationsRankTableBodyCellSx}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => toggle(pid)}
                                        inputProps={{
                                          "aria-label": `Selecionar ${p ? formatPatientListLabel(p) : pid}`,
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={communicationsRankTableBodyCellSx}
                                    >
                                      {p ? (
                                        <PatientNameCell patient={p} />
                                      ) : (
                                        <Tooltip title={`ID: ${pid}`}>
                                          <Typography
                                            component="span"
                                            variant="body2"
                                          >
                                            —
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={communicationsRankTableBodyCellSx}
                                    >
                                      <PatientContactTableCellContent
                                        patient={p}
                                      />
                                    </TableCell>
                                    {!vetApp ? (
                                      <TableCell
                                        align="center"
                                        sx={communicationsRankTableBodyCellSx}
                                      >
                                        <PatientBirthDateTableCellContent
                                          patient={p}
                                        />
                                      </TableCell>
                                    ) : (
                                      <TableCell
                                        align="center"
                                        sx={communicationsRankTableBodyCellSx}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {petNames?.length
                                            ? petNames.join(", ")
                                            : "—"}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    <TableCell
                                      align="center"
                                      sx={communicationsRankTableBodyCellSx}
                                    >
                                      <PatientTaxIdentifierTableCellContent
                                        patient={p}
                                      />
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={communicationsRankTableBodyCellSx}
                                    >
                                      <Tooltip title="Enviar mensagem via WhatsApp">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          aria-label={`Enviar mensagem via WhatsApp para ${p ? formatPatientListLabel(p) : pid}`}
                                          onClick={() =>
                                            openSendForPatientIds([pid])
                                          }
                                        >
                                          <WhatsAppIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <CommunicationsTablePagination
                          count={recipientCount}
                          page={page}
                          rowsPerPage={rowsPerPage}
                          onPageChange={handlePageChange}
                          onRowsPerPageChange={handleRowsPerPageChange}
                        />
                      </>
                    )}
                  </Paper>
                </>
              ) : (
                <NoDataWarning
                  message={
                    vetApp
                      ? "Nenhum tutor vinculado a este disparo."
                      : "Nenhum paciente vinculado a este disparo."
                  }
                />
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            size="medium"
            onClick={onClose}
            sx={{
              ...actionButtonSx,
              bgcolor: "background.paper",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CommunicationsBatchTaskDetailDialog.displayName =
  "CommunicationsBatchTaskDetailDialog";

export default CommunicationsBatchTaskDetailDialog;
